import { z } from 'zod/v4';

import { ActionPermissionEnum } from '../../backend-generated-types';
import { endpoint } from '../http/endpoint';
import { ModuleEnum, RoleAccessLevelEnum } from '../role';
import { CurrentUserUpdateDTORuntype, UserDTORuntype } from '../user/userBackendTypes';
import {
    CurrentUserDetailsDTORuntype,
    ListFeatureFlagsResponseRuntype,
    MyPermissionsResponseRuntype,
    VersionInformationDTORuntype,
} from './userBasedBackendTypes';

export const userBasedEndpoints = {
    'GET /my/details': endpoint({
        description: 'Fetches the details of the logged-in user',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: CurrentUserDetailsDTORuntype }),
    }),

    'GET /my/permissions': endpoint({
        description:
            'Fetches the module- and action-level permissions of the logged in user in one response. Only relevant for tenants with advanced role management enabled; for other tenants both lists are empty.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: MyPermissionsResponseRuntype,
    }),

    'GET /action-permission-map': endpoint({
        description:
            'Fetches the mapping of action permissions to their required module-level access combinations. Returns which module roles are needed for each action permission.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            actionPermissionMapping: z.record(
                ActionPermissionEnum,
                z.array(z.tuple([ModuleEnum, RoleAccessLevelEnum])),
            ),
        }),
    }),

    'GET /my/settings/account-security': endpoint({
        description: 'Fetches the account security settings of the logged-in user',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: z.object({ use_mfa: z.boolean() }) }),
    }),

    'GET /my/version': endpoint({
        description: 'Fetches the current version of the backend',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: VersionInformationDTORuntype }),
    }),

    'GET /my/magicbell': endpoint({
        description: "Returns the current user's MagicBell ID and HMAC for the notification inbox",
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: z.object({
                external_id: z.string(),
                hmac: z.string(),
            }),
        }),
    }),

    'GET /my/intercom': endpoint({
        description: "Returns the current user's Intercom ID and HMAC for identity verification",
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: z.object({
                hmac: z.string(),
                user_id: z.string(),
            }),
        }),
    }),

    'PATCH /my/user': endpoint({
        description: 'Updates the details of the logged-in user',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: CurrentUserUpdateDTORuntype,
        responseBody: z.object({ data: UserDTORuntype }),
    }),

    'PATCH /my/settings/account-security': endpoint({
        description: 'Updates the account security settings of the logged-in user',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({ use_mfa: z.boolean() }),
        responseBody: z.object({ data: z.object({ use_mfa: z.boolean() }) }),
        invalidates: ['GET /my/settings/account-security'],
    }),

    'GET /my/feature-flags': endpoint({
        description: 'Fetches feature flags for the logged-in user with user-specific overrides applied',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: ListFeatureFlagsResponseRuntype,
    }),
};
